import {
  Button,
  Card,
  Details,
  Divider,
  FileUpload,
  Heading,
  Icon,
  Paragraph,
  Spinner,
} from '@innovationnorway/cl-react';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clickHere from 'src/assets/img/click-here.svg';
import nextTask from 'src/assets/img/next-task.svg';
import onlineInformation from 'src/assets/img/online-information.svg';
import shareOnline from 'src/assets/img/share-online.svg';
import NoAccess from 'src/components/NoAccess/NoAccess';
import DocumentTitle from 'src/components/DocumentTitle/DocumentTitle';
import useDocumentTitle from 'src/hooks/use-document-title';
import { LOGO_ALLOWED_FILETYPES, useClusterLogo } from 'src/features/actor-profile/hooks/use-cluster-logo';
import { useAuth } from 'src/features/authorization';
import { useCluster } from 'src/features/cluster';
import CreateLinkToOpportunityForm from 'src/features/one-way-in/components/create-link-form/CreateLinkToOpportunityForm';
import css from './EntryLink.module.scss';

const StepHeading = ({ step, children }: { step: number; children: React.ReactNode }) => (
  <Heading level={3} data-size="xs" className="display--flex align-items--center gap--ds-2">
    <span className={css['step-number']}>{step}</span>
    {children}
  </Heading>
);

type KeyPointsCardProps = {
  heading: string;
  points: string[];
  positive?: boolean;
};

const KeyPointsCard = ({ heading, points, positive = false }: KeyPointsCardProps) => (
  <Card className={classNames(css['key-points-card'], { [css['with-card']]: positive })}>
    <Heading level={4} data-size="2xs" className={css['key-points-heading']}>
      {heading}
    </Heading>
    <Divider />
    <ul className={`${css['key-points']} stack-small`}>
      {points.map((point) => (
        <li key={point}>
          <Icon prefix="fasr" iconName={positive ? 'check' : 'xmark'} aria-hidden />
          <Paragraph data-size="sm">{point}</Paragraph>
        </li>
      ))}
    </ul>
  </Card>
);

const EntryLink = () => {
  const { t } = useTranslation('onboarding');
  const { documentTitle } = useDocumentTitle(t('entryLink.Title'));
  const { cluster } = useCluster();
  const { uploadLogo, deleteLogo, logoQuery } = useClusterLogo(cluster?.id as string);
  const initialLogoFiles = useMemo(() => {
    if (!logoQuery.data) return undefined;
    return [
      new File([logoQuery.data.data], logoQuery.data.fileName ?? 'logo', {
        type: logoQuery.data.data.type,
      }),
    ];
  }, [logoQuery.data]);

  const whatHappensSteps = [
    { text: t('entryLink.WhatHappens.Step1'), image: clickHere },
    { text: t('entryLink.WhatHappens.Step2'), image: nextTask },
    { text: t('entryLink.WhatHappens.Step3'), image: onlineInformation },
  ];

  return (
    <main data-size="sm" className={`${css['page']} full-width-layout`}>
      <DocumentTitle title={documentTitle} />
      <div className={css['intro']}>
        <Heading level={1}>{t('entryLink.Title')}</Heading>
        <Paragraph data-size="sm" className={css['content']}>
          {t('entryLink.Intro')}
        </Paragraph>
      </div>

      <Card data-color="neutral" className={css['details']}>
        <Details data-size="sm">
          <Details.Summary>{t('entryLink.WhatIs.Heading')}</Details.Summary>
          <Details.Content>
            <div className={`${css['content']} stack`}>
              <Paragraph data-size="sm">{t('entryLink.WhatIs.Text1')}</Paragraph>
              <Paragraph data-size="sm">{t('entryLink.WhatIs.Text2')}</Paragraph>
              <Paragraph data-size="sm">{t('entryLink.WhatIs.Text3')}</Paragraph>
            </div>
          </Details.Content>
        </Details>
        <Details data-size="sm">
          <Details.Summary>{t('entryLink.WhatHappens.Heading')}</Details.Summary>
          <Details.Content>
            <ol className={css['steps']}>
              {whatHappensSteps.map(({ text, image }, index) => (
                <li key={image}>
                  <span className={css['step-number']}>{index + 1}</span>
                  <Paragraph data-size="sm">{text}</Paragraph>
                  <img className={css['illustration']} alt="" src={image} />
                </li>
              ))}
            </ol>
          </Details.Content>
        </Details>
        <Details data-size="sm">
          <Details.Summary>{t('entryLink.Why.Heading')}</Details.Summary>
          <Details.Content>
            <div className={css['comparison']}>
              <KeyPointsCard
                positive
                heading={t('entryLink.Why.With.Heading')}
                points={[
                  t('entryLink.Why.With.Point1'),
                  t('entryLink.Why.With.Point2'),
                  t('entryLink.Why.With.Point3'),
                ]}
              />
              <KeyPointsCard
                heading={t('entryLink.Why.Without.Heading')}
                points={[
                  t('entryLink.Why.Without.Point1'),
                  t('entryLink.Why.Without.Point2'),
                  t('entryLink.Why.Without.Point3'),
                ]}
              />
            </div>
          </Details.Content>
        </Details>
      </Card>

      <section className={`${css['content']} ${css['how-to']}`}>
        <Heading level={2}>{t('entryLink.HowTo.Heading')}</Heading>

        <div className={css['how-to-steps']}>
          <div className={css['step']}>
            <StepHeading step={1}>{t('entryLink.Step1.Heading')}</StepHeading>
            <CreateLinkToOpportunityForm />
          </div>

          <Divider />

          <div className={css['step']}>
            <StepHeading step={2}>{t('entryLink.Step2.Heading')}</StepHeading>
            <Paragraph data-size="sm">{t('entryLink.Step2.Text')}</Paragraph>
            <div className="auto-grid auto-grid--15">
              <Card>
                <Heading level={4} data-size="2xs">
                  {t('entryLink.Step2.Material.Heading')}
                </Heading>
                <Paragraph data-size="sm">{t('entryLink.Step2.Material.Text')}</Paragraph>
                <div>
                  <Button asChild variant="secondary" data-size="sm">
                    <a href="/EnVeiInnMateriale.zip" download>
                      <Icon prefix="fasr" iconName="file-arrow-down" aria-hidden />
                      {t('entryLink.Step2.Material.Button')}
                    </a>
                  </Button>
                </div>
              </Card>
              <Card>
                <Heading level={4} data-size="2xs">
                  {t('entryLink.Step2.OwnDesign.Heading')}
                </Heading>
                <Paragraph data-size="sm">{t('entryLink.Step2.OwnDesign.Text')}</Paragraph>
              </Card>
            </div>
          </div>

          <Divider />

          <div className={css['step']}>
            <StepHeading step={3}>{t('entryLink.Step3.Heading')}</StepHeading>
            <Paragraph data-size="sm">{t('entryLink.Step3.Text')}</Paragraph>
            <img
              className={`${css['illustration']} ${css['illustration--large']}`}
              alt=""
              src={shareOnline}
            />
          </div>

          <Divider />

          <div className={css['step']}>
            <StepHeading step={4}>{t('entryLink.Step4.Heading')}</StepHeading>
            <div className={css['logo-upload']}>
              <Paragraph data-size="sm">{t('entryLink.Step4.Text')}</Paragraph>
              {logoQuery.isLoading ? (
                <Spinner aria-label={t('entryLink.Step4.UploadLabel')} />
              ) : (
                <FileUpload
                  label={t('entryLink.Step4.UploadLabel')}
                  description={t('entryLink.Step4.Description')}
                  multiple={false}
                  deletionCallback={deleteLogo}
                  onFilesSelected={uploadLogo}
                  maxSizeInBytes={1 * 1024 * 1024}
                  accept={LOGO_ALLOWED_FILETYPES.join(', ')}
                  initialFiles={initialLogoFiles}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

const EntryLinkPage = () => {
  const { checkAccess } = useAuth();

  if (!checkAccess('OneWayIn').read) {
    return <NoAccess />;
  }

  return <EntryLink />;
};

export default EntryLinkPage;
